import React, {
  Fragment,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import intl from 'react-intl-universal';
import PropTypes from 'prop-types';
import { StatusCodes } from 'http-status-codes';
import { Typography, Modal } from '@mui/material';
import Stack from '@mui/material/Stack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DescriptionIcon from '@mui/icons-material/Description';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import CloseIcon from '@mui/icons-material/Close';
import logger from 'utils/logger';
import Grid from '@mui/material/Grid2';
import api from 'utils/api';
import localData from 'utils/localData';
import useApi from 'shared/hooks/api';
import { PageError, Modal as CustomizedModal } from 'components';
import {
  IssueStatus,
  IssueState,
  IssueStatusToKey,
} from 'shared/constants/issues';
import { ServiceType } from 'shared/constants/service';
import { ConfirmModal } from 'shared/modals/confirmModal';
import ConfirmModalWithConsent from 'components/ConfirmModalWithConsent';
import { USER_DATA } from 'shared/constants/users';

import Loader from './utils/Loader';
import Info from './components/Info';
import Comments from './components/Comments';
import Status from './components/Status';
import AssigneesReporter from './components/AssigneesReporter';
import Priority from './components/Priority';
import EstimateTracking from './components/EstimateTracking';
import Dates from './components/Dates';
import Download from './components/Download';
import Viewport from './components/Viewport';
import Preview from './components/Preview';
import Reports from './components/Reports';
import { ModalForUploadingMoreAttachments } from './components/UploadMoreAttachmentsModal';
import {
  TopActions,
  TopActionsRight,
  Content,
  Left,
  Right,
  PreviewModalPaper,
  ViewportModalPaper,
  GlobalStyles,
} from './Styles';
import { copyToClipboard, checkIfIssueHasDicomFiles } from './utils/utils';
import { useMaterialUIController } from 'context';
import { USER_COMMENT } from 'shared/constants/comment';
import { PATH_PREFIX } from './utils/constants';
import { DOLPHIN_API } from 'shared/constants/apis';
import {
  isCurrentUserPicAsDoctor,
  isCurrentUserTaskManager,
} from 'utils/privilege';
import TabsController from 'components/TabManager';
import New from 'views/New';
import { modalWidths, modalHeights, zIndexValues } from 'utils/styles';
import AttachmentList from './components/AttachmentList';
import {
  useNavigate,
  useParams,
  useOutletContext,
  useLocation,
} from 'react-router-dom';

import AIAnalysis from './components/AIAnalysis';
import { FETCH_STATUS } from './components/AIAnalysis/hooks/useAIAnalytics';
import { useSnackbar } from 'notistack';
import LinkedIssues from './components/LinkedIssues';

import IssueOptionsMenu from './components/IssueOptionsMenu';
import { doesUserHaveAiSummaryPrivilege } from 'utils/privilege';
import { SPECIAL_TAGS } from 'shared/constants/tags';
import { UserType } from 'shared/constants/users';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { WarningToggle } from './components/WarningToggle';

// Create a logger instance for this component
const log = logger.createLogger('IssueDetails');

export const MODAL_TYPES = {
  SWITCH_CHANGE: 'switchChange',
  UPDATE_ISSUE: 'updateIssue',
  ATTACH_TO_PARENT_ISSUE: 'attachToParentIssue',
  ISSUE_SHARE: 'issueShare',
};

const TRIGGER = {
  LEFT: 'ArrowLeft',
  RIGHT: 'ArrowRight',
  BY_DROP: 'byDrop',
  KEYDOWN: 'keydown',
};

const DIRECTIONS = {
  LEFT: 'left',
  RIGHT: 'right',
};

const IssueDetails = () => {
  const { issueId, isPrevIssue } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const trigger = location.state?.trigger || 'byClick';
  const context = useOutletContext();
  const { doctors, updateLocalProjectIssues, issues } = context;

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [openCopyButtonTooltip, setOpenCopyButtonTooltip] = useState(false);
  const [isAIEnabled, setIsAIEnabled] = useState(false);
  const [viewportModalOpen, setViewModalOpen] = useState(false);
  const [viewerDicomFilePaths, setViewerDicomFilePaths] = useState([]);

  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [attachmentObjToBeDownloaded, setAttachmentObjToBeDownloaded] =
    useState({});
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [attachmentObjToBePreviewed, setAttachmentObjToBePreviewed] = useState(
    {}
  );
  // Confirm Modal states
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalWithConsentOpen, setConfirmModalWithConsentOpen] =
    useState(false);
  const [confirmModalType, setConfirmModalType] = useState('');
  const [confirmModalUpdateData, setConfirmModalUpdateData] = useState();
  const [confirmModalInfo, setConfirmModalInfo] = useState({
    source: '',
    destination: '',
  });
  const shareIssueConfirmModalBodyText = useRef(null);
  const shareIssueConfirmFunc = useRef(null);
  const shareIssueCancelFunc = useRef(null);
  const [
    attachToParentIssueConfirmMessage,
    setAttachToParentIssueConfirmMessage,
  ] = useState('');
  const [attachToParentIssueAttachmentId, setAttachToParentIssueAttachmentId] =
    useState(-1);
  const [uploadMoreAttachmentsModalOpen, setUploadMoreAttachmentsModalOpen] =
    useState(false);
  const [, setAttachToParentIssueConfirmModalOpen] = useState(false);
  const [nextIssue, setNextIssue] = useState(null);
  const [previousIssue, setPreviousIssue] = useState(null);

  // new child issue
  const [newChildIssueModalOpen, setNewChildIssueModalOpen] = useState(false);

  // for comment functionalities
  const [refreshQuote, doRefreshQuote] = useState(0);
  const [quoteAttachment, setQuoteAttachment] = useState(null);
  const [loadingIssue, setLoadingIssue] = useState(true);

  const commentEditorRef = useRef(null);
  const [isCommentFormOpen, setIsCommentFormOpen] = useState(false);
  const [AnalyticData, setAnalyticData] = useState({
    fetchStatus: FETCH_STATUS.STARTED,
    data: null,
  });
  const modalContentRef = useRef(null);

  // Reset
  useEffect(() => {
    // Reset all modal states
    setViewModalOpen(false);
    setDownloadModalOpen(false);
    setPreviewModalOpen(false);
    setConfirmModalOpen(false);
    setConfirmModalWithConsentOpen(false);
    setUploadMoreAttachmentsModalOpen(false);
    setNewChildIssueModalOpen(false);

    // Reset navigation states
    setNextIssue(null);
    setPreviousIssue(null);

    // Reset AI data
    setAnalyticData({
      fetchStatus: FETCH_STATUS.STARTED,
      data: null,
    });
  }, [issueId]);

  const isTaskManager = isCurrentUserTaskManager();
  const currentOrganizationId = localData.get(USER_COMMENT.CURRENT_ORGID);

  // memoized to prevent useEffect re-runs
  const startAIAnalysis = useCallback(
    async (forceRerun = false) => {
      try {
        const res = await api.post(`${DOLPHIN_API.NARWHAL_ANALYSIS}`, {
          params: {
            username: localData.get(USER_DATA.USERNAME),
            userId: localData.get(USER_DATA.USER_ID),
            issueId: issueId,
            organizationId: localData.get(USER_COMMENT.CURRENT_ORGID),
            language: localData.get(USER_DATA.PREFFERED_LANGUAGE),
            forceRerun: forceRerun,
          },
        });
        if (res.status === FETCH_STATUS.COMPLETED) {
          setAnalyticData({
            fetchStatus: FETCH_STATUS.COMPLETED,
            data: res,
          });
        } else {
          setAnalyticData({
            fetchStatus: FETCH_STATUS.IN_PROGRESS,
            data: null,
          });
        }
      } catch (error) {
        log.error('Error fetching AI data:', error);
      }
    },
    [issueId]
  );

  const getAIAnalysisResults = useCallback(async () => {
    try {
      const res = await api.get(`${DOLPHIN_API.NARWHAL_ANALYSIS}`, {
        params: {
          username: localData.get(USER_DATA.USERNAME),
          userId: localData.get(USER_DATA.USER_ID),
          issueId: issueId,
          organizationId: localData.get(USER_COMMENT.CURRENT_ORGID),
          language: localData.get(USER_DATA.PREFFERED_LANGUAGE),
        },
      });
      if (res.status === FETCH_STATUS.COMPLETED) {
        setAnalyticData({
          fetchStatus: FETCH_STATUS.COMPLETED,
          data: res,
        });
      }
    } catch (error) {
      log.error('Error fetching AI status:', error);
    }
  }, [issueId]);

  const rerunAIAnalysis = useCallback(async () => {
    // Reset the analytics data state to STARTED to trigger the whole flow
    setAnalyticData({
      fetchStatus: FETCH_STATUS.IN_PROGRESS,
      data: null,
    });

    // Call startAIAnalysis with forceRerun = true
    await startAIAnalysis(true);
  }, [startAIAnalysis]);

  //! There might be case that the issue switches constantly for some reason
  const [{ data, error, setLocalData }, fetchIssue] = useApi.useGet(
    `${DOLPHIN_API.ISSUES}/${issueId}`,
    {
      params: {
        organizationId: localData.get(USER_COMMENT.CURRENT_ORGID),
      },
    },
    {
      retryCount: 3,
      retryDelay: 1000,
    }
  );

  const navigateToIssue = (issueId) => {
    if (loadingIssue) return;
    setLoadingIssue(true);
    const currentPath = window.location.pathname;
    const newPath = currentPath.split('/issues/')[0];
    const newPathWithIssueId = `${newPath}/issues/${issueId}`;
    navigate(newPathWithIssueId);
  };

  //! Currently not using previous issue
  const [, setPrevIssueId] = useState(-1);

  useEffect(() => {
    if (data) {
      setLoadingIssue(false);
    }
    // Adding focus to the modal content when it opens
    if (data && modalContentRef.current) {
      // Focus the modal content when it opens
      modalContentRef.current.focus();

      // Find first focusable element and focus it
      const firstFocusableElement = modalContentRef.current.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }
    }
    if (!isPrevIssue && data) {
      const getPrevIssueId = async () => {
        const res = await api.get(
          `${DOLPHIN_API.ISSUE_SEQUENCE_PREVIOUS_ISSUE_ID}/${data.issue.id}`,
          {
            params: { organizationId: currentOrganizationId },
          }
        );
        setPrevIssueId(res.prevIssueId);
      };
      getPrevIssueId();
    }
  }, [data]);

  useEffect(() => {
    if (issues && issues.length > 0) {
      const currentIssueIndex = issues.findIndex(
        (issue) => issue.id === parseInt(issueId)
      );
      if (currentIssueIndex > -1) {
        setNextIssue(issues[currentIssueIndex + 1]);
        setPreviousIssue(issues[currentIssueIndex - 1]);
      } else {
        log.warn('currentIssueIndex not found', issueId);
      }
    }
  }, [issueId, issues]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle arrow keys when no input fields are focused
      if (
        event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA'
      ) {
        return;
      }

      switch (event.key) {
        case TRIGGER.LEFT:
          if (previousIssue) {
            event.preventDefault();
            navigateToIssue(previousIssue.id, DIRECTIONS.LEFT);
          }
          break;
        case TRIGGER.RIGHT:
          if (nextIssue) {
            event.preventDefault();
            navigateToIssue(nextIssue.id, DIRECTIONS.RIGHT);
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener(TRIGGER.KEYDOWN, handleKeyDown);

    return () => {
      document.removeEventListener(TRIGGER.KEYDOWN, handleKeyDown);
    };
  }, [previousIssue, nextIssue, navigateToIssue]);

  if (error && error.status === StatusCodes.NOT_FOUND) {
    //! Lets send an email with error
    const handleError = async () => {
      try {
        await api.post(DOLPHIN_API.ERROR_LOG, {
          data: {
            userID: localData.get(USER_DATA.USER_ID),
            organizationID: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
            time: new Date().toISOString(),
            errorCode: `Trying to access issue details page with issueId: ${issueId} and error code: ${error.code}`,
            errorMessage: `${error.message} - ${
              import.meta.env.VITE_APP_VERSION
            }`,
            errorDescription: error.description,
            history:
              '[This error was caused from the trying to access the issue details page]',
          },
        });
      } catch (error) {
        log.error('Error sending error log:', error);
      }
    };
    handleError();
    navigate('/home');
  }
  if (!data) return <Loader />;
  if (error)
    return (
      <PageError
        errorCode={error.response.status}
        customErrorMessage={error.response.data.message}
        customErrorDescription={error.response.data.description}
      />
    );

  const { issue } = data;
  const srcOrgId = issue.srcOrgId.toString();
  const dstOrgId = issue.dstOrgId.toString();
  const isClient = parseInt(srcOrgId) === parseInt(currentOrganizationId);
  const shouldAllowOpenViewer = checkIfIssueHasDicomFiles(issue);

  // Being a doctor also means that the user has doctor previlege
  const isDoctor =
    localStorage.getItem(USER_DATA.USER_TYPE) === UserType.DOCTOR ||
    isCurrentUserPicAsDoctor();

  const issueIsDone = issue.status === IssueStatus.DONE;

  const tabs = [
    {
      label: intl.get('issue_details_button_normal_description'),
      icon: <DescriptionIcon />,
    },
    {
      label: intl.get('issue_details_button_ai_description'),
      icon: <AutoAwesomeIcon />,
    },
  ];

  const updateLocalIssueDetails = (fields) => {
    setLocalData((currentData) => ({
      issue: { ...currentData.issue, ...fields },
    }));
  };

  const updateIssue = (updatedFields) => {
    if (trigger === TRIGGER.BY_DROP) {
      if (updatedFields.users) {
        updatedFields = {
          status: IssueStatus.DOCTORASSIGNED,
          ...updatedFields,
        };
      }
    }
    api.optimisticUpdate(`${DOLPHIN_API.ISSUES}/${issueId}`, {
      updatedFields,
      currentFields: issue,
      setLocalData: (fields) => {
        updateLocalIssueDetails(fields);
        updateLocalProjectIssues(issue.id, fields);
      },
    });
  };

  const handleDicomPreviewButtonClick = (dicomFilePaths) => {
    setViewerDicomFilePaths(dicomFilePaths);
    setViewModalOpen(true);
  };

  // for connecting ohif-viewer and docloud
  const handleViewerButtonClick = (attachmentId) => {
    const organizationId = currentOrganizationId;
    const viewerUrl = `${
      import.meta.env.VITE_OHIF_VIEWER_INTERNAL_URL
    }/viewer/dicomDocloudJson?attachmentId=${attachmentId}&issueId=${issueId}&organizationId=${organizationId}&_t=${Date.now()}`;
    window.open(viewerUrl, `viewerTab-${issueId}`, 'popup');
  };

  const handleOpenAllViewerButtonClick = () => {
    const organizationId = currentOrganizationId;
    const hasMammographyTag = issue.tags.some((tag) => {
      const tagName = tag?.content?.name?.toLowerCase();
      return (
        tagName === SPECIAL_TAGS.MAMMOGRAPHY ||
        tagName === SPECIAL_TAGS.BREAST_MRI
      );
    });
    const viewerUrl = hasMammographyTag
      ? `${
          import.meta.env.VITE_OHIF_VIEWER_INTERNAL_URL
        }/viewer/dicomDocloudJson?issueId=${issueId}&organizationId=${organizationId}&hangingProtocolId=mammography-protocol&_t=${Date.now()}`
      : `${
          import.meta.env.VITE_OHIF_VIEWER_INTERNAL_URL
        }/viewer/dicomDocloudJson?issueId=${issueId}&organizationId=${organizationId}&_t=${Date.now()}`;
    window.open(viewerUrl, `viewerTab-${issueId}`, 'popup');
  };

  // Handles comment button
  const handleCommentButtonClick = (attachmentObj) => {
    setQuoteAttachment(attachmentObj);
    doRefreshQuote((prev) => prev + 1);
    setIsCommentFormOpen(true);
  };

  const handleClearQuoteAttachment = () => {
    setQuoteAttachment(null);
  };

  const handleDownloadButtonClick = (attachmentObj) => {
    setAttachmentObjToBeDownloaded(attachmentObj);
    setDownloadModalOpen(true);
  };

  const handlePreviewButtonClick = (attachmentObj) => {
    setAttachmentObjToBePreviewed(attachmentObj);
    setPreviewModalOpen(true);
  };

  const handleAddAttachmentToParentIssueButtonClick = (attachmentObj) => {
    const attachmentId = attachmentObj.id;
    const message =
      intl.get('issue_details_confirm_modal_text_attach_to_parent_issue') +
      `\n${attachmentObj.title}`;
    setAttachToParentIssueAttachmentId(attachmentId);
    setAttachToParentIssueConfirmMessage(message);
    setAttachToParentIssueConfirmModalOpen(true);
  };

  const attachToParentIssue = async () => {
    const issueId = issue.parentIssue.id;
    const attachmentsOwnedByDstOrgIds =
      issue.parentIssue.attachmentsOwnedByDstOrgIds;
    const newAttachmentsOwnedByDstOrgIds = [
      ...attachmentsOwnedByDstOrgIds,
      attachToParentIssueAttachmentId,
    ];
    await api.put(`${DOLPHIN_API.ISSUES}/${issueId}`, {
      params: {
        organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
      },
      data: {
        attachmentsOwnedByDstOrgIds: newAttachmentsOwnedByDstOrgIds,
      },
    });

    setAttachToParentIssueConfirmModalOpen(false);
  };

  const handleBlockQuoteTextPreviewClick = (attachmentName, attachmentId) => {
    const attachmentElement = document.querySelector(
      `[data-attachment-id="${attachmentId}"]`
    );

    if (attachmentElement) {
      attachmentElement.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
      attachmentElement.classList.add('highlight-attachment');
      setTimeout(() => {
        attachmentElement.classList.remove('highlight-attachment');
      }, 2000);
    } else {
      enqueueSnackbar(
        intl.get('issue_details_comment_attachment_deleted', {
          attachmentName,
        }),
        {
          variant: 'error',
        }
      );
    }
  };

  const reloadIssue = async () => {
    await fetchIssue();
  };

  const handleAddAttachmentButtonClick = (state) => {
    setUploadMoreAttachmentsModalOpen(state);
  };
  // Closes the switch
  const handleIssueStateSwitchChange = (formerState) => {
    updateIssue({
      state:
        formerState === IssueState.NORMAL
          ? IssueState.WARNING
          : IssueState.NORMAL,
    });
    setConfirmModalType('');
    setConfirmModalOpen(false);
  };

  const handleDelete = async () => {
    setConfirmModalWithConsentOpen(false);

    await api.delete(`${DOLPHIN_API.ISSUES}/${issueId}`, {
      params: { organizationId: currentOrganizationId },
    });

    enqueueSnackbar(intl.get('issue_details_delete_issue_success'), {
      variant: 'success',
    });
    const currentPath = window.location.pathname;
    const newPath = currentPath.split('/issues/')[0];
    navigate(newPath, { state: { shouldRefresh: true } });
  };

  const handleCopyClick = (text) => {
    copyToClipboard(text);
    setOpenCopyButtonTooltip(true);
    setTimeout(() => {
      setOpenCopyButtonTooltip(false);
    }, 2000);
  };

  const generateConfirmMessage = (confirmModalInfo) => {
    if (confirmModalInfo.issueState === IssueState.NORMAL) {
      if (confirmModalInfo.AssignType === 'assign') {
        if (confirmModalInfo.doctorSelected) {
          return `${confirmModalInfo.doctorSelected.firstName} ${
            confirmModalInfo.doctorSelected.lastName
          }\n${intl.get('kanban_confirm_popup_window_text_assign_doctor')}
        `;
        }
        return intl.get('kanban_confirm_popup_window_text_assign_doctor');
      } else if (confirmModalInfo.AssignType === 'unassign') {
        return intl.get('kanban_confirm_popup_window_text_unassign_doctor');
      }
      if (confirmModalInfo.destination === IssueStatus.CHECKREADY) {
        return intl.get('kanban_confirm_popup_window_text_check_ready');
      } else if (confirmModalInfo.destination === IssueStatus.DOCTORASSIGNED) {
        return intl.get('kanban_confirm_popup_window_text_doctor_assigned');
      } else if (confirmModalInfo.destination === IssueStatus.REPORTREADY) {
        return intl.get('kanban_confirm_popup_window_text_report_ready');
      } else if (confirmModalInfo.destination === IssueStatus.SENDBACK) {
        return intl.get('kanban_confirm_popup_window_text_send_back');
      } else if (confirmModalInfo.destination === IssueStatus.DONE) {
        return intl.get('kanban_confirm_popup_window_text_done');
      }
    } else if (confirmModalInfo.issueState === IssueState.WARNING) {
      const fieldName = intl.get(
        IssueStatusToKey[confirmModalInfo.destination]
      );
      return (
        intl.get('kanban_confirm_popup_window_change_status_with_warning') +
        '\n' +
        '\n' +
        fieldName +
        '\n' +
        '\n' +
        intl.get(
          'kanban_confirm_popup_window_change_status_with_warning_detail'
        )
      );
    }
    return 'undefined message';
  };

  // Add the modal content and rotate between
  const modalContent = {
    [MODAL_TYPES.SWITCH_CHANGE]: {
      title: intl.get('issue_state_change_confirm_modal_title'),
      onConfirm: () => {
        handleIssueStateSwitchChange(issue.state);
      },
    },
    [MODAL_TYPES.ISSUE_SHARE]: {
      title: intl.get('issue_details_confirm_modal_title_share_issue'),
      bodyText: shareIssueConfirmModalBodyText.current,
      onConfirm: shareIssueConfirmFunc.current,
      onCancel: shareIssueCancelFunc.current,
      confirmButtonText: intl.get(
        'issue_details_confirm_modal_button_share_issue_confirm'
      ),
    },
    [MODAL_TYPES.UPDATE_ISSUE]: {
      title: intl.get('issue_details_confirm_modal_title_update_issue'),
      bodyText: generateConfirmMessage(confirmModalInfo),
      onConfirm: () => {
        updateIssue(confirmModalUpdateData);
        setConfirmModalOpen(false);
      },
    },
    [MODAL_TYPES.ATTACH_TO_PARENT_ISSUE]: {
      title: intl.get(
        'issue_details_confirm_modal_title_attach_to_parent_issue'
      ),
      bodyText: attachToParentIssueConfirmMessage,
      onConfirm: () => {
        attachToParentIssue();
      },
    },
  };

  function handleTabChange(value) {
    setIsAIEnabled(value === 1);
  }

  //! SubTask creation only allowed receiver org, the status is new task and the user has task manager privilege
  const canEditIssueDoctorsAndSubTasks =
    isTaskManager === true && !isClient && issue.status !== IssueStatus.NEWTASK;

  const canCheckParentIssue =
    isTaskManager === true &&
    isClient &&
    issue.parentIssue &&
    issue.parentIssue !== undefined;

  // Add buttons to the issue details ovelay on the left and right
  return (
    <>
      <GlobalStyles />
      <CustomizedModal
        isOpen
        testid="modal:issue-details"
        width={modalWidths.xl}
        minWidth={modalWidths.xl}
        withCloseIcon={false}
        onClose={() => {
          const currentPath = window.location.pathname;

          let newPath = currentPath;
          for (const prefix of Object.values(PATH_PREFIX)) {
            if (currentPath.includes(prefix)) {
              newPath = currentPath.split(prefix)[0];
              break;
            }
          }
          log.info('leave new Path', newPath);
          navigate(newPath);
        }}
        zIndex={zIndexValues.modal3}
        renderContent={(modal) =>
          loadingIssue === true ? (
            <Loader />
          ) : (
            <Fragment>
              <div ref={modalContentRef}>
                {previousIssue && (
                  <IconButton
                    onClick={() =>
                      navigateToIssue(previousIssue.id, DIRECTIONS.LEFT)
                    }
                    sx={{
                      position: 'fixed',
                      left: '20px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: zIndexValues.modal3 + 1,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                      },
                      width: '48px',
                      height: '48px',
                    }}
                    aria-label="Previous issue"
                    data-testid="issue-navigation-prev">
                    <ChevronLeftIcon />
                  </IconButton>
                )}

                {nextIssue && (
                  <IconButton
                    onClick={() =>
                      navigateToIssue(nextIssue.id, DIRECTIONS.RIGHT)
                    }
                    sx={{
                      position: 'fixed',
                      right: '20px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: zIndexValues.modal3 + 1,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                      },
                      width: '48px',
                      height: '48px',
                    }}
                    aria-label="Next issue"
                    data-testid="issue-navigation-next">
                    <ChevronRightIcon />
                  </IconButton>
                )}

                {/* Visual queues for the warning state */}
                <div
                  style={{
                    width: '100%',
                    height: '4px',
                    backgroundColor:
                      issue.state === IssueState.WARNING
                        ? '#f44336'
                        : 'transparent',
                    position: 'sticky',
                    top: 0,
                  }}
                />

                <TopActions>
                  <Grid container spacing={1} alignItems="center">
                    <Grid>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          data-testid="kanban-issueDetails-issueId"
                          variant="h6"
                          gutterBottom
                          component="div"
                          style={{
                            fontWeight: 'bold',
                            marginTop: '5px',
                            marginLeft: '14px',
                            textAlign: 'right',
                            alignSelf: 'stretch',
                          }}>
                          {issue.title}
                        </Typography>
                        <Tooltip
                          title={intl.get('issue_details_button_copy_link')}
                          open={openCopyButtonTooltip}
                          arrow
                          leaveDelay={200}>
                          <IconButton
                            onClick={() => handleCopyClick(issue.title)}
                            aria-label="copy">
                            <ContentCopyIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Grid>
                    {doesUserHaveAiSummaryPrivilege() && (
                      <Grid>
                        <TabsController
                          tabs={tabs}
                          onChange={handleTabChange}
                        />
                      </Grid>
                    )}
                  </Grid>
                  <TopActionsRight>
                    <WarningToggle
                      issue={issue}
                      setConfirmModalType={setConfirmModalType}
                      setConfirmModalOpen={setConfirmModalOpen}
                      issueIsDone={issueIsDone}
                    />

                    <IssueOptionsMenu
                      setIssueShareConfirmModalOpen={(status) => {
                        setConfirmModalType(
                          status ? MODAL_TYPES.ISSUE_SHARE : ''
                        );
                        setConfirmModalOpen(status);
                      }}
                      shareIssueConfirmModalBodyText={
                        shareIssueConfirmModalBodyText
                      }
                      shareIssueConfirmFunc={shareIssueConfirmFunc}
                      shareIssueCancelFunc={shareIssueCancelFunc}
                      shareIssueServiceType={ServiceType.SEIMITSU}
                      canDeleteIssue={
                        issue.status === IssueStatus.NEWTASK &&
                        issue.state === IssueState.NORMAL &&
                        isClient
                      }
                      setIssueDeleteConfirmModalOpen={() => {
                        //! Can only delete issue if there are no children issues
                        if (issue.childrenIssues.length === 0) {
                          setConfirmModalWithConsentOpen(true);
                        } else {
                          enqueueSnackbar(
                            intl.get(
                              'issue_details_delete_issue_children_issues'
                            ),
                            {
                              variant: 'warning',
                            }
                          );
                        }
                      }}
                    />
                    <IconButton onClick={modal.close} aria-label="close">
                      <CloseIcon />
                    </IconButton>
                  </TopActionsRight>
                </TopActions>

                <Content>
                  {isAIEnabled ? (
                    <Fragment>
                      <AIAnalysis
                        analyticData={AnalyticData}
                        fetchData={startAIAnalysis}
                        issue={issue}
                        fetchStatus={getAIAnalysisResults}
                        rerunDataAnalysis={rerunAIAnalysis}
                      />
                    </Fragment>
                  ) : (
                    <>
                      {/* Normal Diagnosis */}
                      <Left>
                        <Info issue={issue} />
                        <Divider />
                        {/* Viewer Open Button upper */}
                        <AttachmentList
                          attachments={issue.attachments}
                          handleOpenAllViewerButtonClick={
                            handleOpenAllViewerButtonClick
                          }
                          issue={issue}
                          isDoctor={isDoctor}
                          handleCommentButtonClick={handleCommentButtonClick}
                          handleDownloadButtonClick={handleDownloadButtonClick}
                          handleDicomPreviewButtonClick={
                            handleDicomPreviewButtonClick
                          }
                          handlePreviewButtonClick={handlePreviewButtonClick}
                          handleViewerButtonClick={handleViewerButtonClick}
                          handleAddAttachmentToParentIssueButtonClick={
                            handleAddAttachmentToParentIssueButtonClick
                          }
                          handleAddAttachmentButtonClick={
                            handleAddAttachmentButtonClick
                          }
                          shouldAllowAddSenderAttachment={
                            !issueIsDone && currentOrganizationId === srcOrgId
                          }
                          shouldAllowAddReceiverAttachment={
                            !issueIsDone && currentOrganizationId === dstOrgId
                          }
                          reloadIssue={reloadIssue}
                        />

                        <Divider />
                        <Reports
                          issue={issue}
                          isClient={isClient}
                          isDoctor={isDoctor}
                          reloadIssue={reloadIssue}
                          updateIssue={updateIssue}
                          openViewer={handleOpenAllViewerButtonClick}
                          shouldAllowOpenViewer={shouldAllowOpenViewer}
                        />

                        <Divider />

                        <Comments
                          issue={issue}
                          fetchIssue={fetchIssue}
                          isClient={isClient}
                          updateLocalProjectIssues={updateLocalIssueDetails}
                          quoteAttachment={quoteAttachment}
                          refreshQuote={refreshQuote}
                          commentEditorRef={commentEditorRef}
                          isCommentFormOpen={isCommentFormOpen}
                          setIsCommentFormOpen={setIsCommentFormOpen}
                          handleClearQuoteAttachment={
                            handleClearQuoteAttachment
                          }
                          handlePreviewButtonClick={
                            handleBlockQuoteTextPreviewClick
                          }
                        />
                      </Left>
                      <Right>
                        <Status
                          isClient={isClient}
                          isDoctor={isDoctor}
                          issue={issue}
                          setConfirmModalInfo={setConfirmModalInfo}
                          setConfirmModalUpdateData={setConfirmModalUpdateData}
                          setConfirmModalOpen={() => {
                            setConfirmModalType(MODAL_TYPES.UPDATE_ISSUE);
                            setConfirmModalOpen(true);
                          }}
                        />
                        {canEditIssueDoctorsAndSubTasks && (
                          <AssigneesReporter
                            issue={issue}
                            doctors={doctors.length > 0 ? doctors : issue.users}
                            setConfirmModalInfo={setConfirmModalInfo}
                            setConfirmModalUpdateData={
                              setConfirmModalUpdateData
                            }
                            setConfirmModalOpen={() => {
                              setConfirmModalType(MODAL_TYPES.UPDATE_ISSUE);
                              setConfirmModalOpen(true);
                            }}
                          />
                        )}

                        <Priority issue={issue} updateIssue={updateIssue} />
                        <EstimateTracking issue={issue} />
                        <Dates issue={issue} />

                        {/*! Linked Issues */}
                        <LinkedIssues
                          childrenIssues={issue.childrenIssues}
                          canCreateLinkedIssue={canEditIssueDoctorsAndSubTasks}
                          canCheckParentIssue={canCheckParentIssue}
                          parentIssue={issue.parentIssue}
                          setNewChildIssueModalOpen={setNewChildIssueModalOpen}
                        />
                      </Right>
                    </>
                  )}
                </Content>

                {/* Delete Issue Modal */}
                <ConfirmModalWithConsent
                  isOpened={confirmModalWithConsentOpen}
                  onCancel={() => setConfirmModalWithConsentOpen(false)}
                  onConfirm={handleDelete}
                  title={intl.get('issue_details_delete_issue_title')}
                  bodyText={intl.get('issue_details_delete_issue_body')}
                  confirmButtonText={intl.get('issue_details_button_delete')}
                  cancelButtonText={intl.get('issue_details_button_cancel')}
                />

                {/* Viewport Modal */}
                <Modal
                  open={viewportModalOpen}
                  onClose={() => setViewModalOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: zIndexValues.modal4,
                  }}>
                  <ViewportModalPaper>
                    <Viewport
                      isKenshin={false}
                      dicomFilePaths={viewerDicomFilePaths}
                      close={() => setViewModalOpen(false)}
                    />
                  </ViewportModalPaper>
                </Modal>

                {/* Make an object and then do the modal */}
                {/* The Modal is opened below the issueDetails modal */}

                <ConfirmModal
                  isOpened={confirmModalOpen}
                  title={
                    confirmModalType ? modalContent[confirmModalType].title : ''
                  }
                  bodyText={
                    confirmModalType
                      ? modalContent[confirmModalType].bodyText
                      : null
                  }
                  onConfirm={
                    confirmModalType
                      ? modalContent[confirmModalType].onConfirm
                      : () => {}
                  }
                  onCancel={() => {
                    setConfirmModalType('');
                    setConfirmModalOpen(false);
                  }}
                  confirmButtonColor="info"
                  confirmButtonText={
                    confirmModalType &&
                    modalContent[confirmModalType].confirmButtonText
                      ? modalContent[confirmModalType].confirmButtonText
                      : intl.get('issue_state_change_confirm_modal_confirm_btn')
                  }
                  cancelButtonColor="secondary"
                  cancelButtonText={
                    confirmModalType &&
                    modalContent[confirmModalType].cancelButtonText
                      ? modalContent[confirmModalType].cancelButtonText
                      : intl.get('issue_state_change_confirm_modal_cancel_btn')
                  }
                />
                {/* This part tries to download */}
                {downloadModalOpen && (
                  <Modal
                    open={downloadModalOpen}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: zIndexValues.modal4,
                    }}
                    onClose={() => {
                      setDownloadModalOpen(false);
                    }}>
                    <div>
                      <Download
                        issue={issue}
                        attachment={attachmentObjToBeDownloaded}
                        modalClose={() => setDownloadModalOpen(false)}
                      />
                    </div>
                  </Modal>
                )}

                {/* Preview Modal */}
                <Modal
                  open={previewModalOpen}
                  onClose={() => setPreviewModalOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: zIndexValues.modal4,
                  }}>
                  <PreviewModalPaper>
                    <Preview
                      issue={issue}
                      attachment={attachmentObjToBePreviewed}
                      modalClose={() => setPreviewModalOpen(false)}
                    />
                  </PreviewModalPaper>
                </Modal>

                <ModalForUploadingMoreAttachments
                  issue={issue}
                  fetchIssue={fetchIssue}
                  isOpen={uploadMoreAttachmentsModalOpen}
                  handleModalClose={() =>
                    setUploadMoreAttachmentsModalOpen(false)
                  }
                  darkMode={darkMode}
                />

                {/* Opens a new issue modal */}
                <CustomizedModal
                  isOpen={newChildIssueModalOpen}
                  height={modalHeights.ld}
                  minHeight={modalHeights.lg}
                  width={modalWidths.xl}
                  minWidth={modalWidths.lg}
                  withCloseIcon={true}
                  zIndex={zIndexValues.modal4}
                  onClose={() => {
                    setNewChildIssueModalOpen(false);
                  }}
                  renderContent={(modal) => (
                    <div style={{ padding: '16px' }}>
                      <New
                        parentIssue={issue}
                        reloadIssue={reloadIssue}
                        close={modal.close}
                      />
                    </div>
                  )}
                />
              </div>
            </Fragment>
          )
        }
      />
    </>
  );
};

IssueDetails.propTypes = {
  doctors: PropTypes.array,
  updateLocalProjectIssues: PropTypes.func,
  trigger: PropTypes.string,
};

export default IssueDetails;
